import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Plus, Sunrise, Sun, Moon, BookOpen, Check, ChevronDown, Headphones } from 'lucide-react';

const WINDOW_ICON = { morning: Sunrise, midday: Sun, evening: Moon, any: Clock };
const UNFILED = 'Everything else';

/** One library card, carrying where in the program it came from. */
const CardRow = ({ card, onAdopt }) => {
  const Icon = WINDOW_ICON[card.suggested_window] || Clock;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-2
                    hover:border-amber-300/30 transition-colors duration-200"
         data-testid={`library-card-${card.id}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-medium text-white leading-snug">{card.title}</h3>
        <button
          onClick={() => onAdopt(card)}
          disabled={card.adopted}
          className="shrink-0 inline-flex items-center gap-1 rounded-full border
                     border-amber-300/40 bg-amber-300/10 px-2.5 py-1 text-[11px] text-amber-100
                     hover:bg-amber-300/20 disabled:opacity-40 transition-colors"
          data-testid={`library-adopt-${card.id}`}
        >
          {card.adopted ? <><Check className="w-3 h-3" /> Added</> : <><Plus className="w-3 h-3" /> Add</>}
        </button>
      </div>
      {card.description && (
        <p className="text-xs text-white/50 leading-relaxed line-clamp-3">{card.description}</p>
      )}
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/40">
        <span className="inline-flex items-center gap-1">
          <Icon className="w-3 h-3" />
          {card.suggested_window === 'any' ? 'Any window' : card.suggested_window}
        </span>
        {card.duration_minutes && (
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {card.duration_minutes} min
          </span>
        )}
        {card.audio_url && (
          <span className="inline-flex items-center gap-1 text-amber-200/70"
                data-testid={`library-recording-${card.id}`}>
            <Headphones className="w-3 h-3" /> Guided recording
          </span>
        )}
        {card.source?.label && (
          <Link
            to={card.source.module_id ? `/dashboard/modules/${card.source.module_id}` : '#'}
            className="inline-flex items-center gap-1 text-cyan-200/70 hover:text-cyan-100
                       transition-colors"
            data-testid={`library-source-${card.id}`}
          >
            <BookOpen className="w-3 h-3" /> From {card.source.label}
          </Link>
        )}
      </div>
    </div>
  );
};

/**
 * The ritual library, grouped into categories and closed by default — with a card for every
 * program exercise this list would otherwise run for pages. Searching opens what it matches.
 */
export const RitualLibrary = ({ cards = [], onAdopt, onCreate }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState([]);

  const term = query.trim().toLowerCase();
  const matches = cards.filter((c) =>
    !term || `${c.title} ${c.description} ${c.category}`.toLowerCase().includes(term));

  const groups = matches.reduce((acc, card) => {
    const key = card.category?.trim() || UNFILED;
    (acc[key] = acc[key] || []).push(card);
    return acc;
  }, {});
  const names = Object.keys(groups).sort((a, b) =>
    a === UNFILED ? 1 : b === UNFILED ? -1 : a.localeCompare(b));

  const toggle = (name) =>
    setOpen((o) => (o.includes(name) ? o.filter((n) => n !== name) : [...o, name]));

  return (
    <div className="space-y-4" data-testid="ritual-library">
      <div className="space-y-3">
        <button
          onClick={onCreate}
          className="w-full rounded-xl border border-dashed border-white/20 px-4 py-3 text-sm
                     text-white/70 hover:text-white hover:border-amber-300/50 transition-colors"
          data-testid="ritual-create-custom"
        >
          <Plus className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" /> Write your own ritual
        </button>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the library"
          className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm
                     text-white placeholder-white/25 focus:outline-none focus:border-amber-300/60"
          data-testid="ritual-library-search"
        />
      </div>

      {names.length === 0 ? (
        <p className="text-sm text-white/40 py-6 text-center" data-testid="ritual-library-empty">
          {cards.length
            ? 'Nothing matches that.'
            : 'Ritual cards from the program will gather here as you reach their sections.'}
        </p>
      ) : (
        <div className="space-y-2">
          {names.map((name) => {
            const expanded = term ? true : open.includes(name);
            return (
              <div key={name}
                   className="rounded-xl border border-white/[0.08] bg-white/[0.02] overflow-hidden"
                   data-testid={`library-category-${name}`}>
                <button onClick={() => toggle(name)}
                        className="w-full flex items-center gap-2 px-4 py-3 text-left
                                   hover:bg-white/[0.03] transition-colors"
                        data-testid={`library-category-toggle-${name}`}>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-white truncate">{name}</span>
                    <span className="block text-[11px] text-white/35">
                      {groups[name].length} ritual{groups[name].length === 1 ? '' : 's'}
                      {groups[name].every((c) => c.adopted) ? ' · all added' : ''}
                    </span>
                  </span>
                  <ChevronDown className={`w-4 h-4 text-white/40 transition-transform duration-200
                                           ${expanded ? 'rotate-180' : ''}`} />
                </button>
                {expanded && (
                  <div className="px-3 pb-3 space-y-2.5">
                    {groups[name].map((card) => (
                      <CardRow key={card.id} card={card} onAdopt={onAdopt} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RitualLibrary;
